import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../api/client';
import { T } from '../styles/tokens';
import { Button, Input, Select, ListSkeleton, SkeletonBar } from '../components/ui';

interface BlockedDomain {
  id: string;
  domain: string;
  reason: string;
  added_by: string;
  created_at: string;
}

interface ComplianceStats {
  totalBlockedDomains: number;
  usersWithBlockedDomains: number;
}

export function AdminCompliancePage() {
  const [domains, setDomains] = useState<BlockedDomain[]>([]);
  const [stats, setStats] = useState<ComplianceStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState('');
  const [newReason, setNewReason] = useState('disposable');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [checkEmail, setCheckEmail] = useState('');
  const [checkResult, setCheckResult] = useState<{ blocked: boolean; domain: string } | null>(null);

  const fetchDomains = async () => {
    try {
      const res = await api.get('/admin/compliance/blocked-domains');
      setDomains(res.domains || []);
    } catch { /* ignore */ }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/compliance/stats');
      setStats(res.stats || null);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    Promise.all([fetchDomains(), fetchStats()]).finally(() => setLoading(false));
  }, []);

  const addDomain = async () => {
    if (!newDomain.trim()) return;
    setAdding(true);
    setError('');
    try {
      await api.post('/admin/compliance/blocked-domains', { domain: newDomain.trim(), reason: newReason });
      setNewDomain('');
      await Promise.all([fetchDomains(), fetchStats()]);
    } catch (e: any) {
      setError(e.message || 'Erro ao adicionar');
    }
    setAdding(false);
  };

  const removeDomain = async (id: string) => {
    try {
      await api.delete(`/admin/compliance/blocked-domains/${id}`);
      await Promise.all([fetchDomains(), fetchStats()]);
    } catch { /* ignore */ }
  };

  const handleCheck = async () => {
    if (!checkEmail.includes('@')) return;
    try {
      const res = await api.get(`/admin/compliance/blocked-domains/check?email=${encodeURIComponent(checkEmail)}`);
      setCheckResult(res);
    } catch { /* ignore */ }
  };

  const filtered = search
    ? domains.filter(d => d.domain.includes(search.toLowerCase()))
    : domains;

  const cardStyle: h.JSX.CSSProperties = {
    background: T.bgCard,
    border: `1px solid ${T.border}`,
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '20px',
  };

  const inputStyle: h.JSX.CSSProperties = {
    background: T.bgCardHover,
    border: `1px solid ${T.borderHover}`,
    borderRadius: '10px',
    padding: '10px 14px',
    color: T.border,
    fontSize: '14px',
    outline: 'none',
    flex: 1,
  };

  const btnPrimary: h.JSX.CSSProperties = {
    background: `linear-gradient(135deg, ${T.purple}, ${T.purple})`,
    color: T.text,
    border: 'none',
    borderRadius: '10px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };

  const btnDanger: h.JSX.CSSProperties = {
    background: T.dangerBg,
    color: T.danger,
    border: `1px solid ${T.dangerBgStrong}`,
    borderRadius: '8px',
    padding: '6px 12px',
    fontSize: '12px',
    cursor: 'pointer',
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', color: T.border }}>
        <ListSkeleton rows={3} />
      </div>
    );
  }

  return (
    <div style={{ padding: '30px', maxWidth: '1000px', margin: '0 auto', color: T.border }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
 Compliance
        </h1>
        <p style={{ color: T.textMuted, margin: '8px 0 0', fontSize: '14px' }}>
          Gerenciamento de domínios bloqueados e regras de compliance da plataforma
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div style={{ ...cardStyle, textAlign: 'center', padding: '20px' }}>
            <div style={{ fontSize: '32px', fontWeight: 700, color: T.purple }}>{stats.totalBlockedDomains}</div>
            <div style={{ fontSize: '13px', color: T.textMuted, marginTop: '4px' }}>Domínios Bloqueados</div>
          </div>
          <div style={{ ...cardStyle, textAlign: 'center', padding: '20px' }}>
            <div style={{ fontSize: '32px', fontWeight: 700, color: stats.usersWithBlockedDomains > 0 ? T.danger : T.success }}>
              {stats.usersWithBlockedDomains}
            </div>
            <div style={{ fontSize: '13px', color: T.textMuted, marginTop: '4px' }}>Usuários com Domínio Bloqueado</div>
          </div>
        </div>
      )}

      {/* Check Email */}
      <div style={cardStyle}>
 <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600 }}>Verificar Email</h3>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <input
            type="email"
            placeholder="email@dominio.com"
            value={checkEmail}
            onInput={(e) => setCheckEmail((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCheck()}
            style={inputStyle}
          />
          <Button variant="primary" onClick={handleCheck}>Verificar</Button>
        </div>
        {checkResult && (
          <div style={{
            marginTop: '12px',
            padding: '12px 16px',
            borderRadius: '10px',
            background: checkResult.blocked ? T.dangerBg : T.successBg,
            border: `1px solid ${checkResult.blocked ? T.dangerBg : T.successBg}`,
            color: checkResult.blocked ? T.danger : T.success,
            fontSize: '14px',
          }}>
            {checkResult.blocked
 ? `Domínio @${checkResult.domain} está BLOQUEADO`
 : `Domínio @${checkResult.domain} está liberado`
            }
          </div>
        )}
      </div>

      {/* Add Domain */}
      <div style={cardStyle}>
 <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600 }}>Adicionar Domínio à Blocklist</h3>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            placeholder="dominio.com"
            value={newDomain}
            onInput={(e) => setNewDomain((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => e.key === 'Enter' && addDomain()}
            style={{ ...inputStyle, maxWidth: '300px' }}
          />
          <select
            value={newReason}
            onChange={(e) => setNewReason((e.target as HTMLSelectElement).value)}
            style={{ ...inputStyle, maxWidth: '180px', flex: 'none' }}
          >
            <option value="disposable">Descartável</option>
            <option value="spam">Spam</option>
            <option value="fraud">Fraude</option>
            <option value="other">Outro</option>
          </select>
          <Button variant="primary" onClick={addDomain} disabled={adding} loading={adding}>
            {adding ? 'Adicionando...' : 'Adicionar'}
          </Button>
        </div>
        {error && (
 <div style={{ marginTop: '10px', color: T.danger, fontSize: '13px' }}> {error}</div>
        )}
      </div>

      {/* Domain List */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
 Domínios Bloqueados ({filtered.length})
          </h3>
          <input
            placeholder="Filtrar..."
            value={search}
            onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
            style={{ ...inputStyle, maxWidth: '220px', flex: 'none' }}
          />
        </div>

        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: T.borderHover, fontSize: '14px' }}>
            {search ? 'Nenhum domínio encontrado' : 'Nenhum domínio bloqueado'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {filtered.map((d) => (
              <div key={d.id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 16px',
                background: T.bgSubtle,
                borderRadius: '10px',
                border: `1px solid ${T.borderSubtle}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 500 }}>@{d.domain}</span>
                  <span style={{
                    fontSize: '11px',
                    padding: '2px 8px',
                    borderRadius: '6px',
                    background: d.reason === 'fraud' ? T.dangerBg : d.reason === 'spam' ? T.warningBg : T.purpleBg,
                    color: d.reason === 'fraud' ? T.danger : d.reason === 'spam' ? T.warning : T.purple,
                  }}>
                    {d.reason}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '12px', color: T.borderHover }}>
                    {new Date(d.created_at + 'Z').toLocaleDateString('pt-BR')}
                  </span>
                  <Button variant="danger" size="sm" onClick={() => removeDomain(d.id)}>Remover</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
